import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackLead } from "@/lib/analytics";
import { Phone, Mail, Clock, Send, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, SectionHeader } from "@/components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EQUIPMENT_OPTIONS, AMOUNT_OPTIONS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  fullName: "",
  companyName: "",
  phone: "",
  email: "",
  equipmentType: "",
  requestedAmount: "",
  notes: "",
};

const QuoteForm = ({ prefill }) => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, ...prefill }));
  }, [prefill]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, form);
      trackLead();
      setStatus("success");
      toast.success("Demande envoyée! Nous vous rappelons sous 24 à 48 h.");
    } catch {
      setStatus("idle");
      toast.error("Une erreur est survenue. Appelez-nous directement au (514) 651-3779.");
    }
  };

  const inputCls =
    "bg-[#F7F9FC] border-slate-900/15 text-slate-900 placeholder:text-slate-600 focus-visible:ring-sky-400 h-12";

  return (
    <section id="contact" data-testid="quote-form-section" className="relative py-24 lg:py-32">
      <div className="absolute inset-0 spotlight" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          caption="Passez à l'action"
          title="Vérifiez votre admissibilité maintenant"
          subtitle="Remplissez ce formulaire en deux minutes. Un courtier dédié vous rappelle avec une réponse ferme — pas un « on va vous revenir »."
        />

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 mt-14">
          <Reveal>
            <div className="h-full rounded-2xl border border-slate-900/10 bg-white spotlight-card p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <h3 className="font-display text-3xl font-bold uppercase text-slate-900">
                  Vous préférez en parler de vive voix?
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-4">
                  Un humain répond. Pas un robot, pas un centre d'appel — votre courtier,
                  qui connaît le marché de l'équipement au Québec.
                </p>
                <div className="space-y-5 mt-8">
                  <a
                    data-testid="contact-phone-link"
                    href={PHONE_TEL}
                    className="flex items-center gap-4 group"
                  >
                    <span className="flex items-center justify-center w-11 h-11 rounded-full bg-sky-400/15 border border-sky-400/30 text-sky-600 group-hover:bg-sky-400 group-hover:text-[#0A1220] transition-colors duration-300">
                      <Phone className="w-5 h-5" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-9000">Téléphone direct</span>
                      <span className="block text-lg font-bold text-slate-900 group-hover:text-sky-600 transition-colors">{PHONE_DISPLAY}</span>
                    </span>
                  </a>
                  <a
                    data-testid="contact-email-link"
                    href={`mailto:${EMAIL}`}
                    className="flex items-center gap-4 group"
                  >
                    <span className="flex items-center justify-center w-11 h-11 rounded-full bg-slate-900/5 border border-slate-900/15 text-slate-600 group-hover:border-sky-400 group-hover:text-sky-600 transition-colors duration-300">
                      <Mail className="w-5 h-5" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-9000">Courriel</span>
                      <span className="block text-sm font-semibold text-slate-900 break-all">{EMAIL}</span>
                    </span>
                  </a>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center justify-center w-11 h-11 rounded-full bg-slate-900/5 border border-slate-900/15 text-slate-600">
                      <Clock className="w-5 h-5" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-9000">Heures d'ouverture</span>
                      <span className="block text-sm font-semibold text-slate-900">7 jours sur 7, de 6 h à 20 h</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-10 rounded-xl border border-emerald-400/25 bg-emerald-400/5 p-5 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  Vos informations restent confidentielles et ne servent qu'à analyser votre
                  dossier. Aucune vérification de crédit sans votre consentement.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-sky-400/25 bg-white p-8 lg:p-10">
              {status === "success" ? (
                <div data-testid="quote-form-success" className="h-full min-h-[420px] flex flex-col items-center justify-center text-center">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400" />
                  <h3 className="font-display text-3xl font-bold uppercase text-slate-900 mt-6">
                    Demande bien reçue!
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mt-4 max-w-sm">
                    Merci {form.fullName.split(" ")[0]}. Votre dossier est entre bonnes mains —
                    un courtier vous rappelle au {form.phone} sous 24 à 48 h ouvrables.
                  </p>
                  <a
                    data-testid="success-call-button"
                    href={PHONE_TEL}
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-sky-400/50 px-6 py-3 text-sm font-semibold text-sky-600 hover:bg-sky-400 hover:text-[#0A1220] transition-colors duration-300"
                  >
                    <Phone className="w-4 h-4" />
                    Pressé? Appelez-nous maintenant
                  </a>
                </div>
              ) : (
                <form data-testid="quote-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="fullName" className="text-slate-600">Nom complet *</Label>
                    <Input id="fullName" data-testid="quote-form-fullname-input" required minLength={2}
                      placeholder="Jean Tremblay" value={form.fullName} onChange={set("fullName")} className={`${inputCls} mt-2`} />
                  </div>
                  <div>
                    <Label htmlFor="companyName" className="text-slate-600">Nom de l'entreprise *</Label>
                    <Input id="companyName" data-testid="quote-form-company-input" required minLength={2}
                      placeholder="Excavation Tremblay Inc." value={form.companyName} onChange={set("companyName")} className={`${inputCls} mt-2`} />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-600">Numéro de téléphone *</Label>
                    <Input id="phone" data-testid="quote-form-phone-input" required minLength={7} type="tel"
                      placeholder="(514) 000-0000" value={form.phone} onChange={set("phone")} className={`${inputCls} mt-2`} />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-600">Courriel professionnel *</Label>
                    <Input id="email" data-testid="quote-form-email-input" required type="email"
                      placeholder="jean@excavationtremblay.ca" value={form.email} onChange={set("email")} className={`${inputCls} mt-2`} />
                  </div>
                  <div>
                    <Label className="text-slate-600">Type d'équipement recherché *</Label>
                    <Select required value={form.equipmentType} onValueChange={set("equipmentType")}>
                      <SelectTrigger data-testid="quote-form-equipment-select" className={`${inputCls} mt-2`}>
                        <SelectValue placeholder="Choisir une catégorie" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-900/15 text-slate-900">
                        {EQUIPMENT_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600">Montant estimé ($ CAD) *</Label>
                    <Select required value={form.requestedAmount} onValueChange={set("requestedAmount")}>
                      <SelectTrigger data-testid="quote-form-amount-select" className={`${inputCls} mt-2`}>
                        <SelectValue placeholder="Choisir une fourchette" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-900/15 text-slate-900">
                        {AMOUNT_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="notes" className="text-slate-600">Précisions ou modèle recherché (optionnel)</Label>
                    <Textarea id="notes" data-testid="quote-form-notes-input" rows={4}
                      placeholder="Détails sur l'équipement neuf ou d'occasion..."
                      value={form.notes} onChange={set("notes")}
                      className="bg-[#F7F9FC] border-slate-900/15 text-slate-900 placeholder:text-slate-600 focus-visible:ring-sky-400 mt-2" />
                  </div>
                  <div className="sm:col-span-2">
                    <button
                      data-testid="quote-form-submit-button"
                      type="submit"
                      disabled={status === "sending"}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 px-8 py-4 text-base font-bold text-[#0A1220] hover:bg-sky-300 disabled:opacity-60 transition-colors duration-300"
                    >
                      {status === "sending" ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Envoi en cours...</>
                      ) : (
                        <><Send className="w-5 h-5" /> Faire une demande d'approbation</>
                      )}
                    </button>
                    <p className="text-center text-xs text-slate-9000 mt-4">
                      Réponse ferme en 24 à 48 h ouvrables. Sans engagement de votre part.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
